/**
 * CheckUploadModal — Record a payment against a deal.
 *
 * Originally check-only (RM uploads check photos at the install meeting).
 * Expanded May 7, 2026 to support non-check methods (Stripe, ACH, wire, cash)
 * for back-dated entries when Shannon needs to log a payment that didn't
 * arrive via the check-photo path or the Stripe webhook.
 *
 * Method behavior:
 *   - check  → unchanged. Front + back photos required, uploaded as
 *              check_1_front / check_1_back to deal-documents. State starts
 *              at 'collected'. iink_required toggle drives the 8-state
 *              endorsement path vs. the 3-state direct-deposit path.
 *   - stripe → manual backfill. State starts at 'cleared'
 *              (stamps stripe_cleared_at). Reference field captures the
 *              Stripe charge / payment-intent ID if known. Optional
 *              supporting doc.
 *   - ach    → state 'cleared' (stamps bank_cleared_at). Reference for
 *              ACH confirmation #. Optional supporting doc.
 *   - wire   → state 'cleared'. Reference for wire confirmation #.
 *   - cash   → state 'cleared'. Reference optional.
 *
 * Non-check rows land in Shannon's "Push to QB" bucket on the Finance Board
 * (state='cleared' AND no qb_payment_id), where she can sync them to QBO.
 * iink_required is forced false for non-check.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 3, §10e — original
 * check flow). Non-check support added May 7, 2026 ad hoc when Chuck Foley's
 * payment needed to be recorded so Chad could approve commissions.
 *
 * Component name preserved (CheckUploadModal) so call sites in DealPaymentsTab
 * keep working without churn. Title in the UI is "Record payment".
 *
 * Usage:
 *   <CheckUploadModal dealId={deal.id} onClose={() => setOpen(false)} onCreated={reload} />
 */

import { useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { PaymentKind, PaymentMethod, PaymentSlot, PaymentState, DealPayment } from "../../lib/types";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { heicToJpeg } from "../../lib/heicToJpeg";
import { useAuth } from "../../context/AuthContext";

type Kind = Exclude<PaymentKind, null>;

const SLOTS: Array<{ value: PaymentSlot; label: string }> = [
  { value: "pre_build",  label: "Pre-build payment (install meeting)" },
  { value: "post_build", label: "Post-build / final invoice" },
  { value: "supplement", label: "Supplement payment" },
  { value: "other",      label: "Other" },
];

const METHODS: Array<{ value: PaymentMethod; label: string; sub: string }> = [
  { value: "check",  label: "Check",  sub: "Photo upload" },
  { value: "stripe", label: "Stripe", sub: "Manual backfill" },
  { value: "ach",    label: "ACH",    sub: "Bank transfer" },
  { value: "wire",   label: "Wire",   sub: "Wire transfer" },
  { value: "cash",   label: "Cash",   sub: "Cash in hand" },
];

// Reference-field labels per non-check method. (Field is always free text;
// these are just UX hints so it's clear what to paste in.)
const REF_META: Record<Exclude<PaymentMethod, "check">, { label: string; placeholder: string }> = {
  stripe: { label: "Stripe charge / intent ID (optional)", placeholder: "ch_… or pi_…" },
  ach:    { label: "ACH confirmation # (optional)",        placeholder: "e.g. ACH-12345" },
  wire:   { label: "Wire confirmation # (optional)",       placeholder: "Bank wire ref" },
  cash:   { label: "Reference (optional)",                 placeholder: "Receipt #, etc." },
};

export default function CheckUploadModal({
  dealId,
  onClose,
  onCreated,
}: {
  dealId: string;
  onClose: () => void;
  onCreated?: (payment: DealPayment) => void;
}) {
  const { member } = useAuth();
  const [method, setMethod] = useState<PaymentMethod>("check");
  const [slot, setSlot] = useState<PaymentSlot>("pre_build");
  const [kind, setKind] = useState<Kind>("insurance");
  const [iinkRequired, setIinkRequired] = useState<boolean>(true);
  const [amount, setAmount] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile]   = useState<File | null>(null);
  const [supportFile, setSupportFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const frontRef   = useRef<HTMLInputElement>(null);
  const backRef    = useRef<HTMLInputElement>(null);
  const supportRef = useRef<HTMLInputElement>(null);

  const isCheck = method === "check";

  // When the check kind flips, default iink_required appropriately. RM can override after.
  function setKindWithDefault(k: Kind) {
    setKind(k);
    if (isCheck) setIinkRequired(k === "insurance");
  }

  // Method change: reset photos / iink so they don't leak across modes.
  function handleMethodChange(m: PaymentMethod) {
    setMethod(m);
    if (m !== "check") {
      setFrontFile(null);
      setBackFile(null);
      setIinkRequired(false);
    } else {
      setSupportFile(null);
      setReference("");
      setIinkRequired(kind === "insurance");
    }
  }

  // Check starts at 'collected' so it walks the iink/deposit lifecycle.
  // Non-check methods are entered after the money already arrived → 'cleared',
  // which lands in Shannon's Push-to-QB bucket on the Finance Board.
  function initialStateForMethod(m: PaymentMethod): PaymentState {
    return m === "check" ? "collected" : "cleared";
  }

  function methodLabel(m: PaymentMethod): string {
    return ({ check: "Check", stripe: "Stripe", ach: "ACH", wire: "Wire", cash: "Cash" } as Record<PaymentMethod, string>)[m];
  }

  async function handleSubmit() {
    setError(null);
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError("Amount required.");
      return;
    }
    if (isCheck && (!frontFile || !backFile)) {
      setError("Both front and back photos are required for a check.");
      return;
    }

    setSubmitting(true);
    try {
      // deal_payments.created_by references team_roster(id), not auth.users(id).
      const userId = member?.id ?? null;
      const initialState = initialStateForMethod(method);
      const nowIso = new Date().toISOString();

      // Reference (Stripe ID / ACH conf / wire ref) is folded into notes prefixed
      // with the method label so it surfaces wherever notes render. v1 keeps the
      // schema unchanged; if reference becomes a heavy lookup we'll add a column.
      const referenceText = reference.trim();
      const composedNotes = [
        referenceText ? `${methodLabel(method)} ref: ${referenceText}` : null,
        notes.trim() || null,
      ].filter(Boolean).join(" — ") || null;

      // Compose the row. Stamp the relevant per-state timestamp so the
      // CheckTrackerCard / Finance Board tiles display dates correctly.
      // Stripe webhook stamps stripe_cleared_at on real Stripe payments;
      // mirror that for manual Stripe backfills. Other non-check methods use
      // bank_cleared_at — closest match for "money is in the account."
      const collected_at      = initialState === "collected" ? nowIso : null;
      const stripe_cleared_at = initialState === "cleared" && method === "stripe"  ? nowIso : null;
      const bank_cleared_at   = initialState === "cleared" && method !== "stripe"  ? nowIso : null;

      // 1) Create the deal_payments row first so we can stamp doc records with it.
      const { data: payment, error: paymentErr } = await supabase
        .from("deal_payments")
        .insert({
          deal_id: dealId,
          slot,
          method,
          kind,
          iink_required: isCheck ? iinkRequired : false,
          amount: amt,
          state: initialState,
          notes: composedNotes,
          created_by: userId,
          collected_at,
          stripe_cleared_at,
          bank_cleared_at,
        })
        .select("*")
        .single();
      if (paymentErr) throw paymentErr;

      // Refresh session before any uploads to dodge expired-token issues on mobile
      await supabase.auth.refreshSession();

      if (isCheck) {
        // 2a) Upload front + back photos. Document file_type uses the check_N_*
        //     convention from Ship 2's DOC_TYPES additions. We default to check_1_*
        //     since multi-check workflows are rare; Shannon can rename via deal docs.
        // TODO Ship 11: detect check sequence (check_1 vs check_2) automatically.
        const frontType = "check_1_front";
        const backType  = "check_1_back";
        for (const [rawFile, ftype] of [[frontFile, frontType], [backFile, backType]] as const) {
          // Convert iPhone HEIC/HEIF to JPEG so the check photo is viewable on
          // every device/browser (HEIC won't render in Chrome/Windows).
          const upFile = await heicToJpeg(rawFile as File);
          const path = `${dealId}/${Date.now()}-${upFile.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
          const { error: upErr } = await supabase.storage
            .from("deal-documents")
            .upload(path, upFile, { contentType: upFile.type || "application/octet-stream" });
          if (upErr) throw new Error(`Upload failed (${ftype}): ${upErr.message}`);

          const { error: docErr } = await supabase.from("deal_documents").insert({
            deal_id: dealId,
            file_name: upFile.name,
            file_type: ftype,
            storage_path: path,
            file_size: upFile.size,
            mime_type: upFile.type,
            uploaded_by: userId,
          });
          if (docErr) throw new Error(`Doc insert failed (${ftype}): ${docErr.message}`);
        }
      } else if (supportFile) {
        // 2b) Optional non-check supporting document (ACH conf screenshot,
        //     deposit slip, Stripe receipt, etc.). Logged as DOC_TYPES "other"
        //     so it renders cleanly on the deal Documents tab. file_name keeps
        //     the human-readable context. HEIC→JPEG for cross-browser viewing.
        const upFile = await heicToJpeg(supportFile);
        const path = `${dealId}/${Date.now()}-${upFile.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const { error: upErr } = await supabase.storage
          .from("deal-documents")
          .upload(path, upFile, { contentType: upFile.type || "application/octet-stream" });
        if (upErr) throw new Error(`Upload failed: ${upErr.message}`);

        const { error: docErr } = await supabase.from("deal_documents").insert({
          deal_id: dealId,
          file_name: upFile.name,
          file_type: "other",
          storage_path: path,
          file_size: upFile.size,
          mime_type: upFile.type,
          uploaded_by: userId,
        });
        if (docErr) throw new Error(`Doc insert failed: ${docErr.message}`);
      }

      onCreated?.(payment as DealPayment);
      onClose();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const refMeta = isCheck ? null : REF_META[method as Exclude<PaymentMethod, "check">];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-semibold text-bardin-ink">Record payment</h2>
          <p className="text-xs text-slate-500">
            {isCheck
              ? "Front + back photos required. iink will route automatically based on the box below."
              : "Back-date a payment that didn't come through the check or Stripe path. Saves as cleared, lands in Shannon's Push-to-QB bucket."}
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Method picker */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Method</label>
            <div className="grid grid-cols-5 gap-1">
              {METHODS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => handleMethodChange(m.value)}
                  className={
                    "px-2 py-2 rounded text-xs border text-center transition-colors " +
                    (method === m.value
                      ? "bg-bardin-orange/10 border-bardin-orange text-bardin-orange"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-300")
                  }
                >
                  <div className="font-bold">{m.label}</div>
                  <div className="text-[9px] text-slate-500 mt-0.5">{m.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Slot */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">When was this collected?</label>
            <select
              value={slot}
              onChange={(e) => setSlot(e.target.value as PaymentSlot)}
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            >
              {SLOTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Kind */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Payment type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setKindWithDefault("insurance")}
                className={
                  "flex-1 px-3 py-2 rounded text-sm font-medium border " +
                  (kind === "insurance"
                    ? "bg-bardin-orange/10 border-bardin-orange text-bardin-orange"
                    : "bg-white border-slate-200 text-slate-600")
                }
              >
                {isCheck ? "Insurance check" : "Insurance"}
              </button>
              <button
                onClick={() => setKindWithDefault("personal")}
                className={
                  "flex-1 px-3 py-2 rounded text-sm font-medium border " +
                  (kind === "personal"
                    ? "bg-bardin-orange/10 border-bardin-orange text-bardin-orange"
                    : "bg-white border-slate-200 text-slate-600")
                }
              >
                {isCheck ? "HO personal check" : "Homeowner personal"}
              </button>
            </div>
          </div>

          {/* iink_required toggle — only shown for check method */}
          {isCheck && (
            <div className={`rounded-lg border p-3 ${iinkRequired ? "bg-purple-50/40 border-purple-200" : "bg-slate-50 border-slate-200"}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={iinkRequired}
                  onChange={(e) => setIinkRequired(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-purple-600"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800">
                    This check requires iink endorsement
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Look at the back of the check. If the mortgage company is named as a co-payee, leave this checked.
                    If insurance wrote the check directly to Bardin (no mortgage on file), uncheck.
                  </div>
                  {iinkRequired && (
                    <div className="text-[11px] text-purple-700 mt-1.5">🏛 Will route through 8-state iink path: collected → iink → mortgage endorsed → FedEx → … → bank cleared.</div>
                  )}
                  {!iinkRequired && (
                    <div className="text-[11px] text-slate-600 mt-1.5">↩ Will route through simple 3-state path: collected → deposited → bank cleared.</div>
                  )}
                </div>
              </label>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Amount</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
          </div>

          {/* Photos for check, reference + optional doc for non-check */}
          {isCheck ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Front photo</label>
                <input ref={frontRef} type="file" accept="image/*" capture="environment" onChange={(e) => setFrontFile(e.target.files?.[0] ?? null)} className="hidden" />
                <button
                  onClick={() => frontRef.current?.click()}
                  className={
                    "w-full px-3 py-3 rounded border-2 border-dashed text-sm " +
                    (frontFile ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500")
                  }
                >
                  {frontFile ? `✓ ${truncate(frontFile.name, 18)}` : "📷 Tap to capture front"}
                </button>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Back photo</label>
                <input ref={backRef} type="file" accept="image/*" capture="environment" onChange={(e) => setBackFile(e.target.files?.[0] ?? null)} className="hidden" />
                <button
                  onClick={() => backRef.current?.click()}
                  className={
                    "w-full px-3 py-3 rounded border-2 border-dashed text-sm " +
                    (backFile ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500")
                  }
                >
                  {backFile ? `✓ ${truncate(backFile.name, 18)}` : "📷 Tap to capture back"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">{refMeta!.label}</label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={refMeta!.placeholder}
                  className="w-full text-sm border border-slate-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Supporting document (optional)</label>
                <input ref={supportRef} type="file" accept="image/*,application/pdf" onChange={(e) => setSupportFile(e.target.files?.[0] ?? null)} className="hidden" />
                <button
                  onClick={() => supportRef.current?.click()}
                  className={
                    "w-full px-3 py-3 rounded border-2 border-dashed text-sm " +
                    (supportFile ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500")
                  }
                >
                  {supportFile ? `✓ ${truncate(supportFile.name, 28)}` : "📎 Attach screenshot, deposit slip, ACH confirmation…"}
                </button>
              </div>

              <div className="text-[11px] text-slate-600 bg-slate-50 rounded px-3 py-2 border border-slate-200">
                Saves as <strong>cleared</strong> — appears in Shannon's <strong>Push to QB</strong> bucket on the Finance Board, ready for QuickBooks sync.
              </div>
            </>
          )}

          {/* Optional notes */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={isCheck
                ? "e.g. ACV check from State Farm. Mortgage co-payee: Wells Fargo."
                : "Anything Shannon should know about this payment."}
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
          </div>

          {error && <div className="text-xs text-red-700 bg-red-50 rounded px-3 py-2">⚠ {error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} disabled={submitting} className="px-4 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !amount || (isCheck && (!frontFile || !backFile))}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Saving…" : isCheck ? "Save check" : "Save payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
